import React, { useState } from "react";

/**
 * YOUTUBE CHANNEL BANNER — 2048 x 1152 px
 *
 * YouTube safe zones:
 *   - TV / all devices (CENTER):     1235 x 338 px  → guaranteed visible
 *   - Desktop:                       2560 x 423 px  (we render 2048x423 strip)
 *   - Tablet:                        1855 x 423 px
 *   - Mobile:                        1546 x 423 px
 *
 * Anything outside the 1235x338 center zone may be cropped on smaller screens,
 * so the brand-critical copy ALWAYS lives inside that center band.
 *
 * Render modes (via ?mode=... query param):
 *   - default            → preview page with safe-zone overlay + design controls
 *   - export             → bare 2048x1152 banner, no chrome (for screenshotting)
 */

const BRAND = {
  deep:   "#042F2E",  // Sacred Deep
  teal:   "#068fa2",  // True Teal
  ocean:  "#25a1c2",  // Bright Ocean
  gold:   "#d4af6a",  // Antique Gold
  cream:  "#efeae7",  // Paper Cream
  mint:   "#5EEAD4",  // Mint Accent
};

// ============================================================================
// THE BANNER ITSELF — rendered at exact 2048 x 1152 px
// ============================================================================
const Banner = ({ variant = "v1" }) => {
  // Variant A — Sacred Deep background, gold accent
  if (variant === "v1") {
    return (
      <div
        data-testid="yt-banner-v1"
        style={{
          width: "2048px",
          height: "1152px",
          position: "relative",
          background: `linear-gradient(135deg, ${BRAND.deep} 0%, ${BRAND.teal} 65%, ${BRAND.ocean} 100%)`,
          color: BRAND.cream,
          overflow: "hidden",
          fontFamily: "Cormorant Garamond, serif",
        }}
      >
        {/* Soft radial glow */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(circle at 50% 50%, rgba(212, 175, 106, 0.22) 0%, transparent 55%)`,
          }}
        />

        {/* Decorative gold ornaments — outside the safe zone */}
        <div style={{ position: "absolute", top: "120px", left: "180px", color: BRAND.gold, fontSize: "72px", opacity: 0.45 }}>✦</div>
        <div style={{ position: "absolute", bottom: "140px", right: "200px", color: BRAND.gold, fontSize: "72px", opacity: 0.45 }}>✦</div>
        <div style={{ position: "absolute", top: "240px", right: "320px", color: BRAND.gold, fontSize: "32px", opacity: 0.5 }}>✦</div>
        <div style={{ position: "absolute", bottom: "280px", left: "340px", color: BRAND.gold, fontSize: "32px", opacity: 0.5 }}>✦</div>

        {/* ──── CENTER SAFE ZONE (1235 x 338) — guaranteed visible on every device ──── */}
        <div
          data-testid="yt-banner-safe-zone-content"
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: "1235px",
            height: "338px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: "0 40px",
          }}
        >
          {/* Top ornamental rule */}
          <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "18px" }}>
            <div style={{ width: "60px", height: "1px", background: BRAND.gold, opacity: 0.7 }} />
            <span style={{ color: BRAND.gold, fontSize: "20px" }}>✦</span>
            <div style={{ width: "60px", height: "1px", background: BRAND.gold, opacity: 0.7 }} />
          </div>

          {/* Main headline */}
          <h1
            style={{
              fontFamily: "Cormorant Garamond, serif",
              fontWeight: 400,
              fontSize: "108px",
              lineHeight: 1,
              letterSpacing: "-0.02em",
              margin: 0,
              color: BRAND.cream,
              whiteSpace: "nowrap",
            }}
          >
            MIND BODY SPIRIT{" "}
            <span style={{ fontStyle: "italic", color: BRAND.mint }}>
              HYGIENE
              <sup style={{ fontSize: "32px", marginLeft: "4px", verticalAlign: "super" }}>™</sup>
            </span>
          </h1>

          {/* THE METHOD — uppercase kicker beneath */}
          <p
            style={{
              fontFamily: "system-ui, -apple-system, sans-serif",
              fontSize: "26px",
              letterSpacing: "0.55em",
              textTransform: "uppercase",
              margin: "18px 0 0 0",
              color: BRAND.gold,
              fontWeight: 500,
              paddingLeft: "0.55em",
            }}
          >
            The Method
          </p>

          {/* Tagline */}
          <p
            style={{
              fontFamily: "Cormorant Garamond, serif",
              fontStyle: "italic",
              fontSize: "38px",
              lineHeight: 1.2,
              margin: "18px 0 0 0",
              color: BRAND.cream,
              opacity: 0.92,
            }}
          >
            Real tools, for real women, in the messy middle.
          </p>

          {/* Bottom ornamental rule */}
          <div style={{ display: "flex", alignItems: "center", gap: "16px", marginTop: "22px" }}>
            <div style={{ width: "60px", height: "1px", background: BRAND.gold, opacity: 0.7 }} />
            <span style={{ color: BRAND.gold, fontSize: "20px" }}>✦</span>
            <div style={{ width: "60px", height: "1px", background: BRAND.gold, opacity: 0.7 }} />
          </div>
        </div>

        {/* ──── DESKTOP-ONLY ZONE (only shows on big screens) ──── */}
        {/* Bottom-left: URL */}
        <div
          style={{
            position: "absolute",
            bottom: "80px",
            left: "120px",
            fontFamily: "system-ui, -apple-system, sans-serif",
            fontSize: "28px",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: BRAND.mint,
            opacity: 0.85,
          }}
        >
          mindbodyspirithygienetools.com
        </div>

        {/* Bottom-right: handle */}
        <div
          style={{
            position: "absolute",
            bottom: "80px",
            right: "120px",
            fontFamily: "system-ui, -apple-system, sans-serif",
            fontSize: "28px",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: BRAND.mint,
            opacity: 0.85,
          }}
        >
          @TempleFranklin
        </div>

        {/* Top-right: author */}
        <div
          style={{
            position: "absolute",
            top: "100px",
            right: "120px",
            fontFamily: "Cormorant Garamond, serif",
            fontStyle: "italic",
            fontSize: "32px",
            color: BRAND.gold,
          }}
        >
          Temple Franklin
        </div>
      </div>
    );
  }

  // Variant B — Paper Cream background, deep teal text
  if (variant === "v2") {
    return (
      <div
        data-testid="yt-banner-v2"
        style={{
          width: "2048px",
          height: "1152px",
          position: "relative",
          background: BRAND.cream,
          color: BRAND.deep,
          overflow: "hidden",
        }}
      >
        {/* Subtle teal corner washes */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(circle at 0% 0%, rgba(6, 143, 162, 0.12) 0%, transparent 35%),
                         radial-gradient(circle at 100% 100%, rgba(212, 175, 106, 0.18) 0%, transparent 40%)`,
          }}
        />

        {/* Decorative ornaments */}
        <div style={{ position: "absolute", top: "120px", left: "180px", color: BRAND.gold, fontSize: "72px", opacity: 0.5 }}>✦</div>
        <div style={{ position: "absolute", bottom: "140px", right: "200px", color: BRAND.gold, fontSize: "72px", opacity: 0.5 }}>✦</div>

        {/* SAFE ZONE */}
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: "1235px",
            height: "338px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: "0 40px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "18px" }}>
            <div style={{ width: "60px", height: "1px", background: BRAND.gold }} />
            <span style={{ color: BRAND.gold, fontSize: "20px" }}>✦</span>
            <div style={{ width: "60px", height: "1px", background: BRAND.gold }} />
          </div>

          <h1
            style={{
              fontFamily: "Cormorant Garamond, serif",
              fontWeight: 400,
              fontSize: "108px",
              lineHeight: 1,
              letterSpacing: "-0.02em",
              margin: 0,
              color: BRAND.deep,
              whiteSpace: "nowrap",
            }}
          >
            MIND BODY SPIRIT{" "}
            <span style={{ fontStyle: "italic", color: BRAND.teal }}>
              HYGIENE
              <sup style={{ fontSize: "32px", marginLeft: "4px", verticalAlign: "super" }}>™</sup>
            </span>
          </h1>

          <p
            style={{
              fontFamily: "Cormorant Garamond, serif",
              fontStyle: "italic",
              fontSize: "42px",
              lineHeight: 1.2,
              margin: "26px 0 0 0",
              color: BRAND.teal,
            }}
          >
            Real tools, for real women, in the messy middle.
          </p>

          <div style={{ display: "flex", alignItems: "center", gap: "16px", marginTop: "22px" }}>
            <div style={{ width: "60px", height: "1px", background: BRAND.gold }} />
            <span style={{ color: BRAND.gold, fontSize: "20px" }}>✦</span>
            <div style={{ width: "60px", height: "1px", background: BRAND.gold }} />
          </div>
        </div>

        {/* Desktop-only zones */}
        <div
          style={{
            position: "absolute",
            bottom: "80px",
            left: "120px",
            fontFamily: "system-ui, -apple-system, sans-serif",
            fontSize: "28px",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: BRAND.ocean,
            fontWeight: 500,
          }}
        >
          mindbodyspirithygienetools.com
        </div>
        <div
          style={{
            position: "absolute",
            bottom: "80px",
            right: "120px",
            fontFamily: "system-ui, -apple-system, sans-serif",
            fontSize: "28px",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: BRAND.ocean,
            fontWeight: 500,
          }}
        >
          @TempleFranklin
        </div>
        <div
          style={{
            position: "absolute",
            top: "100px",
            right: "120px",
            fontFamily: "Cormorant Garamond, serif",
            fontStyle: "italic",
            fontSize: "32px",
            color: BRAND.teal,
          }}
        >
          Temple Franklin
        </div>
      </div>
    );
  }

  // Variant C — Deep teal with offset ocean accent
  return (
    <div
      data-testid="yt-banner-v3"
      style={{
        width: "2048px",
        height: "1152px",
        position: "relative",
        background: BRAND.deep,
        color: BRAND.cream,
        overflow: "hidden",
      }}
    >
      {/* Diagonal accent stripe */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "-200px",
          right: "-200px",
          width: "900px",
          height: "1600px",
          background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.ocean} 100%)`,
          transform: "rotate(15deg)",
          opacity: 0.35,
        }}
      />

      <div style={{ position: "absolute", top: "120px", left: "180px", color: BRAND.gold, fontSize: "72px", opacity: 0.45 }}>✦</div>
      <div style={{ position: "absolute", bottom: "140px", right: "200px", color: BRAND.gold, fontSize: "72px", opacity: 0.45 }}>✦</div>

      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          width: "1235px",
          height: "338px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: "0 40px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "18px" }}>
          <div style={{ width: "60px", height: "1px", background: BRAND.gold, opacity: 0.7 }} />
          <span style={{ color: BRAND.gold, fontSize: "20px" }}>✦</span>
          <div style={{ width: "60px", height: "1px", background: BRAND.gold, opacity: 0.7 }} />
        </div>

        <h1
          style={{
            fontFamily: "Cormorant Garamond, serif",
            fontWeight: 400,
            fontSize: "108px",
            lineHeight: 1,
            letterSpacing: "-0.02em",
            margin: 0,
            color: BRAND.cream,
            whiteSpace: "nowrap",
          }}
        >
          MIND BODY SPIRIT{" "}
          <span style={{ fontStyle: "italic", color: BRAND.gold }}>
            HYGIENE
            <sup style={{ fontSize: "32px", marginLeft: "4px", verticalAlign: "super" }}>™</sup>
          </span>
        </h1>

        <p
          style={{
            fontFamily: "Cormorant Garamond, serif",
            fontStyle: "italic",
            fontSize: "42px",
            lineHeight: 1.2,
            margin: "26px 0 0 0",
            color: BRAND.mint,
          }}
        >
          Real tools, for real women, in the messy middle.
        </p>

        <div style={{ display: "flex", alignItems: "center", gap: "16px", marginTop: "22px" }}>
          <div style={{ width: "60px", height: "1px", background: BRAND.gold, opacity: 0.7 }} />
          <span style={{ color: BRAND.gold, fontSize: "20px" }}>✦</span>
          <div style={{ width: "60px", height: "1px", background: BRAND.gold, opacity: 0.7 }} />
        </div>
      </div>

      <div style={{ position: "absolute", bottom: "80px", left: "120px", fontFamily: "system-ui, sans-serif", fontSize: "28px", letterSpacing: "0.18em", textTransform: "uppercase", color: BRAND.mint, opacity: 0.85 }}>
        mindbodyspirithygienetools.com
      </div>
      <div style={{ position: "absolute", bottom: "80px", right: "120px", fontFamily: "system-ui, sans-serif", fontSize: "28px", letterSpacing: "0.18em", textTransform: "uppercase", color: BRAND.mint, opacity: 0.85 }}>
        @TempleFranklin
      </div>
      <div style={{ position: "absolute", top: "100px", right: "120px", fontFamily: "Cormorant Garamond, serif", fontStyle: "italic", fontSize: "32px", color: BRAND.gold }}>
        Temple Franklin
      </div>
    </div>
  );
};

// ============================================================================
// SAFE ZONE OVERLAY (for preview only, removed in export mode)
// ============================================================================
const SafeZoneOverlay = () => (
  <>
    {/* TV-safe (center) — guaranteed visible everywhere */}
    <div
      style={{
        position: "absolute",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        width: "1235px",
        height: "338px",
        border: "3px dashed rgba(94, 234, 212, 0.85)",
        pointerEvents: "none",
        boxSizing: "border-box",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: "-32px",
          left: "0",
          fontFamily: "system-ui, sans-serif",
          fontSize: "20px",
          color: BRAND.mint,
          background: "rgba(0,0,0,0.6)",
          padding: "2px 10px",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
        }}
      >
        TV-Safe · 1235 × 338 (always visible)
      </span>
    </div>

    {/* Desktop visible band */}
    <div
      style={{
        position: "absolute",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        width: "2048px",
        height: "423px",
        border: "2px dotted rgba(212, 175, 106, 0.7)",
        pointerEvents: "none",
        boxSizing: "border-box",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: "-32px",
          right: "0",
          fontFamily: "system-ui, sans-serif",
          fontSize: "20px",
          color: BRAND.gold,
          background: "rgba(0,0,0,0.6)",
          padding: "2px 10px",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
        }}
      >
        Desktop band · 2048 × 423
      </span>
    </div>
  </>
);

// ============================================================================
// MAIN PAGE
// ============================================================================
const YouTubeBanner = () => {
  const params = new URLSearchParams(window.location.search);
  const mode = params.get("mode") || "preview";
  const initialVariant = params.get("v") || "v1";
  const [variant, setVariant] = useState(initialVariant);
  const [showOverlay, setShowOverlay] = useState(false);

  // EXPORT MODE — bare banner, no chrome. Screenshot at 2048x1152 viewport.
  if (mode === "export") {
    return (
      <div
        data-testid="yt-banner-export"
        style={{ width: "2048px", height: "1152px", margin: 0, padding: 0 }}
      >
        <Banner variant={variant} />
      </div>
    );
  }

  // Preview scale — fit the 2048-wide banner into the browser
  const scale = 0.5; // shows at 1024 x 576

  return (
    <div
      data-testid="yt-banner-page"
      style={{ minHeight: "100vh", background: "#1a1a1a", color: "#fff", padding: "40px 24px", fontFamily: "system-ui, sans-serif" }}
    >
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        <p style={{ fontSize: "12px", letterSpacing: "0.35em", textTransform: "uppercase", color: BRAND.mint, margin: 0 }}>
          YouTube Channel Banner · 2048 × 1152 px
        </p>
        <h1 style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "44px", lineHeight: 1.1, margin: "8px 0 24px 0" }}>
          Pick a direction. Then we refine.
        </h1>
        <p style={{ color: "#aaa", fontSize: "15px", maxWidth: "720px", lineHeight: 1.5 }}>
          Three variants below — same copy, three moods. The dashed teal box is the
          TV-safe center zone (always visible on every device). The dotted gold band is
          the desktop strip. Anything outside those gets cropped on smaller screens.
        </p>

        {/* CONTROLS */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", margin: "28px 0 18px 0", alignItems: "center" }}>
          {[
            { id: "v1", label: "A · Deep gradient + gold" },
            { id: "v2", label: "B · Cream + teal" },
            { id: "v3", label: "C · Deep + ocean stripe" },
          ].map((v) => (
            <button
              key={v.id}
              data-testid={`yt-variant-${v.id}`}
              onClick={() => setVariant(v.id)}
              style={{
                background: variant === v.id ? BRAND.mint : "transparent",
                color: variant === v.id ? BRAND.deep : "#fff",
                border: `1px solid ${variant === v.id ? BRAND.mint : "#555"}`,
                padding: "10px 18px",
                borderRadius: "999px",
                fontSize: "13px",
                letterSpacing: "0.08em",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              {v.label}
            </button>
          ))}

          <button
            data-testid="yt-toggle-overlay"
            onClick={() => setShowOverlay((s) => !s)}
            style={{
              marginLeft: "auto",
              background: "transparent",
              color: showOverlay ? BRAND.mint : "#aaa",
              border: `1px solid ${showOverlay ? BRAND.mint : "#555"}`,
              padding: "10px 18px",
              borderRadius: "999px",
              fontSize: "13px",
              letterSpacing: "0.08em",
              cursor: "pointer",
            }}
          >
            {showOverlay ? "✓ Safe-zone overlay ON" : "Show safe-zone overlay"}
          </button>
        </div>

        {/* SCALED PREVIEW */}
        <div
          style={{
            width: `${2048 * scale}px`,
            height: `${1152 * scale}px`,
            position: "relative",
            border: "1px solid #444",
            overflow: "hidden",
            background: "#000",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              width: "2048px",
              height: "1152px",
            }}
          >
            <Banner variant={variant} />
            {showOverlay && <SafeZoneOverlay />}
          </div>
        </div>

        {/* EXPORT INSTRUCTIONS */}
        <div style={{ marginTop: "28px", padding: "20px 24px", background: "#111", border: "1px solid #333", borderRadius: "12px", fontSize: "14px", lineHeight: 1.6, color: "#bbb" }}>
          <p style={{ margin: "0 0 8px 0", color: BRAND.mint, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", fontSize: "12px" }}>
            To export the final PNG
          </p>
          <p style={{ margin: 0 }}>
            Once you pick your favorite, tell me which one (A, B, or C) and I'll generate
            the print-quality 2048 × 1152 PNG using the same Playwright pipeline that
            built your 58 card decks. I'll drop it in <code style={{ color: BRAND.gold }}>/downloads/</code> ready to upload to YouTube.
          </p>
          <p style={{ margin: "10px 0 0 0" }}>
            You can also preview the bare banner at full size by visiting
            <code style={{ color: BRAND.gold, marginLeft: 6 }}>/yt-banner?mode=export&v={variant}</code>
            (just right-click → "Save image as" works for a quick test).
          </p>
        </div>

        {/* COPY EDITOR HINT */}
        <div style={{ marginTop: "20px", padding: "20px 24px", background: "#0f1f1e", border: `1px solid ${BRAND.teal}`, borderRadius: "12px", fontSize: "14px", lineHeight: 1.6 }}>
          <p style={{ margin: "0 0 8px 0", color: BRAND.mint, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", fontSize: "12px" }}>
            What's locked vs. easy to change
          </p>
          <p style={{ margin: 0, color: "#ddd" }}>
            <strong style={{ color: BRAND.mint }}>Center (locked-in copy):</strong> "MIND BODY SPIRIT HYGIENE™" + "Real tools, for real women, in the messy middle."
            <br />
            <strong style={{ color: BRAND.mint }}>Corners (easy to swap):</strong> kicker, name, URL, handle — just tell me what to change.
          </p>
        </div>
      </div>
    </div>
  );
};

export default YouTubeBanner;
